import { useCallback, useEffect, useRef, useState } from 'react'
import { Camera, RotateCcw, ScanLine, Check, X, RefreshCw } from 'lucide-react'
import {
  ensureOpenCv,
  detectDocument,
  defaultCorners,
  warpToBlob,
  fileToMat,
  AUTO_CONF,
  type Pt,
} from './documentDetect'

/* OpenCV global — only used here to release Mats on cleanup. */
declare const cv: any

interface DocumentScannerProps {
  /* Called with the flattened (perspective-corrected) page as a JPEG blob. */
  onScanned: (blob: Blob) => void
  /* Called when the user dismisses the scanner without a result. */
  onClose: () => void
}

type Screen = 'loading' | 'camera' | 'edit' | 'result'

const HINTS: Record<Screen, string> = {
  loading: 'Tarama motoru hazırlanıyor…',
  camera: 'Kağıdı düz bir zemine koyun, tüm kenarları görünsün',
  edit: 'Köşeleri sürükleyerek kağıdın kenarlarına hizalayın',
  result: 'Düzleştirilmiş sayfa hazır',
}

export function DocumentScanner({ onScanned, onClose }: DocumentScannerProps) {
  const [screen, setScreen] = useState<Screen>('loading')
  const [corners, setCorners] = useState<Pt[] | null>(null)
  const [processing, setProcessing] = useState<string | null>('OpenCV.js yükleniyor…')
  const [error, setError] = useState<string | null>(null)
  const [meta, setMeta] = useState<{ w: number; h: number; kb: number } | null>(null)

  const photoMatRef = useRef<any>(null)
  const blobRef = useRef<Blob | null>(null)
  const draggingIdxRef = useRef<number>(-1)

  const photoCanvasRef = useRef<HTMLCanvasElement>(null)
  const resultCanvasRef = useRef<HTMLCanvasElement>(null)
  const editWrapRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  /* ── OpenCV bootstrap ─────────────────────────────────────── */
  useEffect(() => {
    let alive = true
    ensureOpenCv()
      .then(() => {
        if (!alive) return
        setProcessing(null)
        setScreen('camera')
      })
      .catch((err) => {
        if (!alive) return
        setProcessing(null)
        setError(err?.message ?? 'OpenCV yüklenemedi')
      })
    return () => {
      alive = false
      releasePhotoMat()
    }
  }, [])

  const releasePhotoMat = () => {
    if (photoMatRef.current) {
      try {
        photoMatRef.current.delete()
      } catch {
        /* ignore double-free on a reduced build */
      }
      photoMatRef.current = null
    }
  }

  /* ── Capture → detect → auto/manual routing ───────────────── */
  const captureFromFile = useCallback(async (file: File | undefined) => {
    if (!file) return
    setProcessing('Fotoğraf yükleniyor…')
    try {
      releasePhotoMat()
      const mat = await fileToMat(file)
      photoMatRef.current = mat

      const canvas = photoCanvasRef.current!
      canvas.width = mat.cols
      canvas.height = mat.rows
      cv.imshow(canvas, mat)

      setProcessing('Belge sınırları aranıyor…')
      // yield so the overlay paints before the heavy CV pass
      await new Promise((r) => setTimeout(r, 50))

      const result = detectDocument(mat)
      const detected = result?.corners ?? null
      const conf = result?.conf ?? 0
      const start = detected ?? defaultCorners(mat.cols, mat.rows)
      setCorners(start)

      // High-confidence detection skips the manual corner screen entirely.
      if (detected && conf >= AUTO_CONF) {
        await warpAndShow(start)
        return
      }

      setProcessing(null)
      setScreen('edit')
    } catch (err: any) {
      setProcessing(null)
      setError('Fotoğraf okunamadı: ' + (err?.message ?? err?.name ?? 'bilinmeyen hata'))
    }
  }, [])

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-picking the same file
    void captureFromFile(file)
  }

  const openNativeCamera = () => fileInputRef.current?.click()

  /* ── Re-run auto detection on the current photo ───────────── */
  const redetect = useCallback(async () => {
    const mat = photoMatRef.current
    if (!mat) return
    setProcessing('Yeniden aranıyor…')
    await new Promise((r) => setTimeout(r, 50))
    const result = detectDocument(mat)
    setProcessing(null)
    if (result) setCorners(result.corners)
  }, [])

  /* ── Perspective warp → result screen ─────────────────────── */
  const warpAndShow = useCallback(async (pts: Pt[]) => {
    const mat = photoMatRef.current
    const canvas = resultCanvasRef.current
    if (!mat || !canvas) return
    setProcessing('Düzleştiriliyor…')
    await new Promise((r) => setTimeout(r, 50))
    try {
      const { blob, width, height } = await warpToBlob(mat, pts, canvas)
      blobRef.current = blob
      setMeta({ w: width, h: height, kb: Math.round(blob.size / 1024) })
      setProcessing(null)
      setScreen('result')
    } catch (err: any) {
      setProcessing(null)
      setError('Düzleştirme başarısız: ' + (err?.message ?? 'bilinmeyen hata'))
    }
  }, [])

  /* ── Corner dragging (pointer events on the edit wrap) ────── */
  const pointerToCanvas = (e: React.PointerEvent): Pt | null => {
    const canvas = photoCanvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return null
    return {
      x: ((e.clientX - rect.left) * canvas.width) / rect.width,
      y: ((e.clientY - rect.top) * canvas.height) / rect.height,
    }
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (!corners) return
    const pt = pointerToCanvas(e)
    if (!pt) return
    const canvas = photoCanvasRef.current!
    const hitR = Math.max(45, canvas.width / 20)
    let nearest = -1
    let nearestDist = hitR
    corners.forEach((c, i) => {
      const d = Math.hypot(c.x - pt.x, c.y - pt.y)
      if (d < nearestDist) {
        nearest = i
        nearestDist = d
      }
    })
    if (nearest >= 0) {
      draggingIdxRef.current = nearest
      try {
        editWrapRef.current?.setPointerCapture(e.pointerId)
      } catch {
        /* setPointerCapture can throw on stale ids */
      }
      e.preventDefault()
    }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const idx = draggingIdxRef.current
    if (idx < 0) return
    const pt = pointerToCanvas(e)
    if (!pt) return
    const canvas = photoCanvasRef.current!
    setCorners((prev) => {
      if (!prev) return prev
      const next = prev.slice()
      next[idx] = {
        x: Math.max(0, Math.min(canvas.width, pt.x)),
        y: Math.max(0, Math.min(canvas.height, pt.y)),
      }
      return next
    })
    e.preventDefault()
  }

  const onPointerUp = (e: React.PointerEvent) => {
    if (draggingIdxRef.current >= 0) {
      try {
        editWrapRef.current?.releasePointerCapture(e.pointerId)
      } catch {
        /* ignore */
      }
      draggingIdxRef.current = -1
    }
  }

  /* ── Screen transitions ───────────────────────────────────── */
  const retake = () => {
    releasePhotoMat()
    setCorners(null)
    setScreen('camera')
    openNativeCamera()
  }

  const useResult = () => {
    if (blobRef.current) onScanned(blobRef.current)
  }

  /* SVG overlay geometry (1:1 with the photo canvas pixel space) */
  const overlayBox = (() => {
    const canvas = photoCanvasRef.current
    if (!canvas || !corners) return null
    const stroke = Math.max(3, canvas.width / 250)
    const handleR = Math.max(20, canvas.width / 45)
    return {
      vb: `0 0 ${canvas.width} ${canvas.height}`,
      points: corners.map((p) => `${p.x},${p.y}`).join(' '),
      stroke,
      handleR,
    }
  })()

  return (
    <div className="m-scan">
      {/* Header */}
      <div className="m-scan__top">
        <button className="m-scan__close" onClick={onClose} aria-label="Kapat">
          <X size={22} />
        </button>
        <div className="m-scan__title">
          <strong>Kağıt Tara</strong>
          <span>{HINTS[screen]}</span>
        </div>
      </div>

      <div className="m-scan__body">
        {error && (
          <div className="m-scan__error">
            <p>{error}</p>
            <button className="m-btn m-btn--soft" onClick={() => setError(null)}>
              Tamam
            </button>
          </div>
        )}

        {/* Camera / start screen */}
        {screen === 'camera' && !error && (
          <div className="m-scan__start">
            <div className="m-scan__start-icon">
              <ScanLine size={56} />
            </div>
            <p>
              Sınav kağıdını düz bir zemine koyun ve tüm kenarları görünecek şekilde fotoğraflayın.
              En keskin sonuç için cihaz kamerası tam çözünürlükte kullanılır.
            </p>
          </div>
        )}

        {/* Edit (corner adjust) screen — canvas always mounted so refs stay live */}
        <div
          className="m-scan__stage"
          style={{ display: screen === 'edit' ? 'block' : 'none' }}
        >
          <div
            className="m-scan__editwrap"
            ref={editWrapRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <canvas ref={photoCanvasRef} className="m-scan__canvas" />
            {overlayBox && (
              <svg
                className="m-scan__overlay"
                viewBox={overlayBox.vb}
                preserveAspectRatio="none"
              >
                <polygon
                  points={overlayBox.points}
                  fill="rgba(37,99,235,0.16)"
                  stroke="#2563eb"
                  strokeWidth={overlayBox.stroke}
                  strokeLinejoin="round"
                />
                {corners!.map((p, i) => (
                  <g key={i}>
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={overlayBox.handleR}
                      fill="rgba(37,99,235,0.4)"
                      stroke="#fff"
                      strokeWidth={overlayBox.stroke}
                    />
                    <circle cx={p.x} cy={p.y} r={overlayBox.handleR * 0.35} fill="#2563eb" />
                  </g>
                ))}
              </svg>
            )}
          </div>
        </div>

        {/* Result screen */}
        <div
          className="m-scan__stage"
          style={{ display: screen === 'result' ? 'block' : 'none' }}
        >
          <div className="m-scan__resultwrap">
            <canvas ref={resultCanvasRef} className="m-scan__canvas" />
          </div>
          {meta && (
            <div className="m-scan__stats">
              <div className="m-scan__stat">
                <b>{meta.w}</b>
                <span>Genişlik</span>
              </div>
              <div className="m-scan__stat">
                <b>{meta.h}</b>
                <span>Yükseklik</span>
              </div>
              <div className="m-scan__stat">
                <b>{meta.kb} KB</b>
                <span>Boyut</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer actions */}
      {!error && screen === 'camera' && (
        <div className="m-scan__actions">
          <button className="m-btn m-btn--primary m-btn--block m-btn--lg" onClick={openNativeCamera}>
            <Camera size={20} /> Sınav Kağıdı Tara
          </button>
        </div>
      )}

      {!error && screen === 'edit' && (
        <div className="m-scan__actions m-scan__actions--row">
          <button className="m-btn m-btn--ghost" onClick={retake}>
            <RotateCcw size={18} /> Tekrar
          </button>
          <button className="m-btn m-btn--ghost" onClick={redetect}>
            <RefreshCw size={18} /> Otomatik
          </button>
          <button
            className="m-btn m-btn--primary"
            onClick={() => corners && warpAndShow(corners)}
          >
            <Check size={18} /> Düzleştir
          </button>
        </div>
      )}

      {!error && screen === 'result' && (
        <div className="m-scan__actions m-scan__actions--row">
          <button className="m-btn m-btn--ghost" onClick={() => setScreen('edit')}>
            <RotateCcw size={18} /> Düzelt
          </button>
          <button className="m-btn m-btn--primary" onClick={useResult}>
            <Check size={18} /> Bu sayfayı ekle
          </button>
        </div>
      )}

      {/* Native full-resolution camera (iOS-friendly) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={onFileChange}
      />

      {/* Processing overlay */}
      {processing && (
        <div className="m-scan__processing">
          <div className="m-spinner" />
          <div>{processing}</div>
        </div>
      )}
    </div>
  )
}
